/**
 * Modal compacto de creación de centro. Extraído de CenterDrawer.tsx
 * para que ese fichero respete el límite de líneas del proyecto.
 *
 * Pide solo lo mínimo: nombre, slug, timezone y email del admin que
 * recibirá la invitación. El resto de campos (contacto, horarios, etc.)
 * se editan luego desde el drawer.
 */
import { useState } from 'react';
import * as Icons from '@components/Icons';

const TIMEZONE_OPTIONS = [
  { group: 'España', options: [
    ['Europe/Madrid', 'Europe/Madrid (Península y Baleares)'],
    ['Atlantic/Canary', 'Atlantic/Canary (Canarias)'],
  ]},
  { group: 'Europa', options: [
    ['Europe/London', 'Europe/London'],
    ['Europe/Paris', 'Europe/Paris'],
    ['Europe/Berlin', 'Europe/Berlin'],
    ['Europe/Rome', 'Europe/Rome'],
    ['Europe/Lisbon', 'Europe/Lisbon'],
  ]},
  { group: 'América', options: [
    ['America/Mexico_City', 'America/Mexico_City'],
    ['America/Bogota', 'America/Bogota'],
    ['America/Lima', 'America/Lima'],
    ['America/Santiago', 'America/Santiago'],
    ['America/Argentina/Buenos_Aires', 'America/Argentina/Buenos_Aires'],
    ['America/Sao_Paulo', 'America/Sao_Paulo'],
  ]},
  { group: 'Resto del mundo', options: [
    ['Asia/Dubai', 'Asia/Dubai'],
    ['Asia/Tokyo', 'Asia/Tokyo'],
    ['UTC', 'UTC'],
  ]},
];

function Req() {
  return <span style={{ color: 'var(--accent)' }}>*</span>;
}

function FormError({ msg }: { msg: string }) {
  return (
    <div style={{
      padding: '10px 14px',
      background: 'var(--danger-soft)',
      border: '1px solid oklch(0.85 0.05 25)',
      borderRadius: 'var(--radius-sm)',
      fontSize: 13,
      color: 'var(--accent-dark)',
      marginBottom: 12,
    }}>
      {msg}
    </div>
  );
}

function TimezoneSelect({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <select className="select" value={value} onChange={(e) => { onChange(e.target.value); }}>
      <option value="">— Selecciona zona horaria —</option>
      {TIMEZONE_OPTIONS.map((grp) => (
        <optgroup key={grp.group} label={grp.group}>
          {grp.options.map(([val, label]) => (
            <option key={val} value={val}>{label}</option>
          ))}
        </optgroup>
      ))}
    </select>
  );
}

interface CenterCreateModalProps {
  onClose: () => void;
  onSave: (data: Record<string, unknown>) => Promise<void>;
  isSaving: boolean;
}

export function CenterCreateModal({ onClose, onSave, isSaving }: CenterCreateModalProps) {
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [timezone, setTimezone] = useState('Europe/Madrid');
  const [adminEmail, setAdminEmail] = useState('');
  const [err, setErr] = useState('');

  async function handleSubmit(e: React.SyntheticEvent) {
    e.preventDefault();
    setErr('');
    if (!name.trim()) { setErr('El nombre es obligatorio.'); return; }
    if (!slug.trim()) { setErr('El slug es obligatorio.'); return; }
    if (!adminEmail.trim()) { setErr('El email del administrador es obligatorio.'); return; }
    if (!timezone) { setErr('La zona horaria es obligatoria.'); return; }
    try {
      await onSave({
        name: name.trim(),
        slug: slug.trim(),
        timezone,
        allowedOrigins: [],
        adminEmail: adminEmail.trim(),
      });
    } catch (e) {
      const msg = e instanceof Error && e.message ? e.message : 'Error al guardar. Inténtalo de nuevo.';
      setErr(msg);
    }
  }

  return (
    <div className="modal-scrim" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 440 }} onClick={(e) => { e.stopPropagation(); }}>
        <div className="modal-head">
          <div className="modal-title">Nuevo centro</div>
          <button className="icon-btn" data-variant="ghost" onClick={onClose}><Icons.X size={14} /></button>
        </div>
        <form className="modal-body" onSubmit={handleSubmit}>
          {err && <FormError msg={err} />}
          <div className="field">
            <label className="field-label">Nombre <Req /></label>
            <input className="input" value={name} onChange={(e) => { setName(e.target.value); }} autoFocus />
          </div>
          <div className="field">
            <label className="field-label">Slug (URL) <Req /></label>
            <input
              className="input mono"
              value={slug}
              onChange={(e) => { setSlug(e.target.value); }}
              placeholder="mi-centro"
            />
            <p className="field-help">Solo minúsculas, números y guiones. No se puede cambiar después.</p>
          </div>
          <div className="field">
            <label className="field-label">Zona horaria <Req /></label>
            <TimezoneSelect value={timezone} onChange={setTimezone} />
          </div>
          <div className="field">
            <label className="field-label">Email del administrador <Req /></label>
            <input
              className="input"
              type="email"
              value={adminEmail}
              onChange={(e) => { setAdminEmail(e.target.value); }}
              placeholder="admin@tucentro.com"
            />
            <p className="field-help">Recibirá un enlace de invitación al crear el centro.</p>
          </div>
          <p className="field-help" style={{ marginTop: 4 }}>
            Tras crear el centro podrás añadir contacto, orígenes permitidos y horarios desde su edición.
          </p>
        </form>
        <div className="modal-foot">
          <button className="btn" onClick={onClose}>Cancelar</button>
          <button className="btn" data-variant="primary" disabled={isSaving} onClick={handleSubmit}>
            {isSaving ? 'Guardando…' : 'Crear centro'}
          </button>
        </div>
      </div>
    </div>
  );
}
